import React, { useState } from "react";
import { X, AlertTriangle, Phone, Heart, Activity, Shield } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import EmergencyBookingForm from "./EmergencyBookingForm";

const CRISIS_TYPES = [
  {
    id: "suicide-ideation",
    label: "Suicidal Thoughts",
    description: "Having thoughts of ending your life",
    icon: Heart,
    color: "red",
    hotline: "0800 720 648 (Befrienders Kenya - 24/7)",
  },
  {
    id: "self-harm",
    label: "Self-Harm",
    description: "Urges to hurt yourself",
    icon: AlertTriangle,
    color: "red",
    hotline: "0800 720 648 (Befrienders Kenya)",
  },
  {
    id: "panic-attack",
    label: "Panic Attack",
    description: "Experiencing severe anxiety or panic",
    icon: Activity,
    color: "orange",
    hotline: "0800 720 648",
  },
  {
    id: "severe-anxiety",
    label: "Severe Anxiety",
    description: "Overwhelming anxiety or fear",
    icon: Activity,
    color: "orange",
    hotline: "0800 720 648",
  },
  {
    id: "trauma-response",
    label: "Trauma Response",
    description: "Recent traumatic experience",
    icon: Shield,
    color: "red",
    hotline: "116 (Gender Violence Recovery Centre - 24/7)",
  },
  {
    id: "domestic-violence",
    label: "Domestic Violence",
    description: "Experiencing abuse at home",
    icon: Shield,
    color: "red",
    hotline: "1195 (Gender-Based Violence Hotline)",
  },
  {
    id: "sexual-assault",
    label: "Sexual Assault",
    description: "Recent sexual assault or abuse",
    icon: Shield,
    color: "red",
    hotline: "116 (GVRC 24/7)",
  },
  {
    id: "substance-abuse-crisis",
    label: "Substance Abuse Crisis",
    description: "Emergency related to substance use",
    icon: AlertTriangle,
    color: "orange",
    hotline: "NACADA: 0800 220 002",
  },
  {
    id: "acute-depression",
    label: "Severe Depression",
    description: "Deep sadness or hopelessness",
    icon: Heart,
    color: "orange",
    hotline: "0800 720 648",
  },
  {
    id: "psychotic-episode",
    label: "Psychotic Episode",
    description: "Loss of contact with reality",
    icon: AlertTriangle,
    color: "red",
    hotline: "999 or 112 (Emergency Services)",
  },
  {
    id: "other-emergency",
    label: "Other Emergency",
    description: "Other urgent mental health concern",
    icon: AlertTriangle,
    color: "orange",
    hotline: "0800 720 648",
  },
];

const EmergencyModal = ({ isOpen, onClose }) => {
  const [selectedCrisis, setSelectedCrisis] = useState(null);
  const [showBookingForm, setShowBookingForm] = useState(false);

  const handleCrisisSelect = (crisis) => {
    setSelectedCrisis(crisis);
    setShowBookingForm(true);
  };

  const handleBack = () => {
    setShowBookingForm(false);
    setSelectedCrisis(null);
  };

  const handleClose = () => {
    setShowBookingForm(false);
    setSelectedCrisis(null);
    onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={handleClose}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50"
          />

          {/* Modal - Properly Centered on Screen */}
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
              {/* Header */}
              <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6 flex-shrink-0">
                <div className="flex justify-between items-center">
                  <div>
                    <h2 className="text-2xl font-bold flex items-center gap-2">
                      <AlertTriangle className="w-7 h-7" />
                      Emergency Support
                    </h2>
                    <p className="text-red-100 mt-1">
                      {showBookingForm
                        ? "Complete your emergency booking"
                        : "Select your situation - help is available"}
                    </p>
                  </div>
                  <button
                    onClick={handleClose}
                    className="text-white hover:bg-white/20 rounded-full p-2 transition"
                  >
                    <X className="w-6 h-6" />
                  </button>
                </div>
              </div>

              {/* Content - Scrollable */}
              <div className="p-6 overflow-y-auto flex-1">
              {/* Critical Notice */}
              <div className="bg-red-50 border-l-4 border-red-600 p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Phone className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold text-red-900">
                      If this is a life-threatening emergency:
                    </p>
                    <p className="text-red-800 text-sm mt-1">
                      Call <strong>999</strong> or <strong>112</strong>{" "}
                      immediately, or go to the nearest hospital emergency
                      department.
                    </p>
                  </div>
                </div>
              </div>

              {!showBookingForm ? (
                <>
                  <p className="text-gray-700 mb-6">
                    Please select the option that best describes your current
                    situation. We will connect you with appropriate support.
                  </p>

                  {/* Crisis Options Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {CRISIS_TYPES.map((crisis) => {
                      const Icon = crisis.icon;
                      const colorClasses =
                        crisis.color === "red"
                          ? "border-red-200 hover:border-red-400 hover:bg-red-50"
                          : "border-orange-200 hover:border-orange-400 hover:bg-orange-50";

                      return (
                        <motion.button
                          key={crisis.id}
                          onClick={() => handleCrisisSelect(crisis)}
                          whileHover={{ scale: 1.02 }}
                          whileTap={{ scale: 0.98 }}
                          className={`text-left p-4 rounded-lg border-2 ${colorClasses} transition-all        
  duration-200`}
                        >
                          <div className="flex items-start gap-3">
                            <Icon
                              className={`w-6 h-6 ${
                                crisis.color === "red"
                                  ? "text-red-600"
                                  : "text-orange-600"
                              } flex-shrink-0 mt-0.5`}
                            />
                            <div className="flex-1">
                              <h3 className="font-semibold text-gray-900">
                                {crisis.label}
                              </h3>
                              <p className="text-sm text-gray-600 mt-1">
                                {crisis.description}
                              </p>
                              {crisis.hotline && (
                                <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                                  <Phone className="w-3 h-3" />
                                  {crisis.hotline}
                                </p>
                              )}
                            </div>
                          </div>
                        </motion.button>
                      );
                    })}
                  </div>
                </>
              ) : (
                <EmergencyBookingForm
                  selectedCrisis={selectedCrisis}
                  onBack={handleBack}
                  onSuccess={handleClose}
                />
              )}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default EmergencyModal;
